import {
  co2eLifecycleLabels,
  costLifecycleLabels,
  isLifecycle,
  Lifecycle,
  lifecycles,
} from '../models/lifecycles.model';
import {
  CO2eUnit,
  CostUnit,
  Results,
  SbefGroupResults,
} from '../models/unit.model';
import { sbefScopeGroupKeys } from '../construction/sbef-custom-groups.data';
import { validateConversionFactorUnit } from '../validation/conversion-factor.validation';
import { isDefined } from './array.helpers';
import { getKeys, omit } from './object.helpers';

const COST_PREFIX = 'sek_';
const CO2E_PREFIX = 'co2e_';

/**
 * Throw error if lifecycle is not valid.
 * @param lifecycle
 * @returns
 */
export const validateLifecycle = (lifecycle: unknown): Lifecycle => {
  if (!isLifecycle(lifecycle)) {
    throw new Error(`Invalid lifecycle: ${String(lifecycle)}`);
  }
  return lifecycle;
};

type LifecycleObject = { stage: string; phase: number };
const singleRegexp = /^([ABCD])(\d+)$/;
const rangeRegexp = /^([ABCD])(\d+)-([ABCD])(\d+)$/;

const parseSingleLifecycle = (lifecycle: string): LifecycleObject | null => {
  const [, stage, phase] = singleRegexp.exec(lifecycle) ?? [];
  if (stage !== undefined && phase !== undefined) {
    return { stage, phase: Number(phase) };
  }
  return null;
};
const parseLifecycle = (lifecycle: string): LifecycleObject[] | [null] => {
  const lifeCycleObj = parseSingleLifecycle(lifecycle);
  if (lifeCycleObj) return [lifeCycleObj];
  if (rangeRegexp.test(lifecycle)) {
    const [start, end] = lifecycle.split('-').map(parseSingleLifecycle);
    if (start === null || end === null)
      throw new Error(`Invalid lifecycle range: ${lifecycle}`);
    if (start && end && start.stage === end.stage && start.phase < end.phase) {
      const expanded: LifecycleObject[] = [];
      for (let i = start.phase; i <= end.phase; i++) {
        expanded.push({ stage: start.stage, phase: i });
      }
      return expanded;
    }
  }
  return [null];
};

/** Phase numbers we support per stage (from {@link lifecycles}), e.g. B has no B3/B5. */
const getSupportedPhasesPerStage = (): Record<string, number[]> => {
  const sets: Record<string, Set<number>> = {};
  for (const lifecycle of lifecycles) {
    for (const parsed of parseLifecycle(lifecycle)) {
      if (!parsed) continue;
      const stagePhases = (sets[parsed.stage] ??= new Set());
      stagePhases.add(parsed.phase);
    }
  }
  return Object.fromEntries(
    Object.entries(sets).map(([stage, phases]) => [
      stage,
      [...phases].sort((a, b) => a - b),
    ]),
  );
};

/**
 * Gaps may only be bridged when every missing phase in between is unsupported
 * (e.g. B3 between B2 and B4), not when a supported phase was deselected.
 */
const isBridgeableGap = (
  supportedPhases: number[],
  fromPhase: number,
  toPhase: number,
): boolean => {
  const supported = new Set(supportedPhases);
  for (let phase = fromPhase + 1; phase < toPhase; phase++) {
    if (supported.has(phase)) return false;
  }
  return true;
};

const formatStagePhaseRange = (
  stage: string,
  start: number,
  end: number,
): string =>
  start === end ? `${stage}${start}` : `${stage}${start}-${stage}${end}`;

const abbreviateStagePhases = (
  stage: string,
  selectedPhases: number[],
  supportedPhases: number[],
): string[] => {
  if (selectedPhases.length === 0) return [];

  const sorted = [...selectedPhases].sort((a, b) => a - b);
  const ranges: string[] = [];
  let start = sorted[0]!;
  let end = start;

  for (let i = 1; i < sorted.length; i++) {
    const current = sorted[i]!;
    if (isBridgeableGap(supportedPhases, end, current)) {
      end = current;
      continue;
    }
    ranges.push(formatStagePhaseRange(stage, start, end));
    start = end = current;
  }

  ranges.push(formatStagePhaseRange(stage, start, end));
  return ranges;
};

const isAllLifecyclesSelected = (
  selectedLifecycles: (Lifecycle | string)[],
): boolean =>
  selectedLifecycles.length === lifecycles.length &&
  lifecycles.every((lifecycle) => selectedLifecycles.includes(lifecycle));

const abbreviateLifecycles = (
  selectedLifecycles: (Lifecycle | string)[],
): string => {
  const supportedPhasesPerStage = getSupportedPhasesPerStage();

  const cyclesMap = Array.from(selectedLifecycles)
    .sort()
    .reduce<Record<string, number[]>>((accumulator, lifecycle) => {
      parseLifecycle(lifecycle)
        .filter(isDefined)
        .forEach(({ stage, phase }) => {
          accumulator[stage] = accumulator[stage] ?? [];
          accumulator[stage][phase] = phase;
        });
      return accumulator;
    }, {});

  return Object.entries(cyclesMap)
    .flatMap(([stage, phases]) => {
      const selectedPhases = phases.flatMap((phase, index) =>
        phase !== undefined ? [index] : [],
      );
      return abbreviateStagePhases(
        stage,
        selectedPhases,
        supportedPhasesPerStage[stage] ?? [],
      );
    })
    .join(', ');
};

export const getLifecycleLabel = (
  selectedLifecycles: (Lifecycle | string)[],
): string => {
  selectedLifecycles.forEach(validateLifecycle);
  if (selectedLifecycles.length === 0) {
    return 'No lifecycles selected';
  }
  if (isAllLifecyclesSelected(selectedLifecycles)) {
    return 'A1-C4';
  }
  return abbreviateLifecycles(selectedLifecycles);
};

/**
 * Get the cost unit (sek_A1-A3 etc) from the lifecycle.
 * @param lifecycle - The lifecycle to get the cost unit from.
 * @returns The cost unit.
 */
export const getCostUnitFromLifecycle = (lifecycle: Lifecycle): CostUnit =>
  validateConversionFactorUnit(
    COST_PREFIX + validateLifecycle(lifecycle),
  ) as CostUnit;

/**
 * Get the CO2e unit (co2e_A1-A3 etc) from the lifecycle.
 * @param lifecycle - The lifecycle to get the CO2e unit from.
 * @returns The CO2e unit.
 */
export const getCO2eUnitFromLifecycle = (lifecycle: Lifecycle): CO2eUnit =>
  validateConversionFactorUnit(
    CO2E_PREFIX + validateLifecycle(lifecycle),
  ) as CO2eUnit;

/**
 * Get all CO2e units belonging to a lifecycle.
 * Unlike {@link getCO2eUnitFromLifecycle} this supports lifecycles with
 * multiple sub-units (B1 -> co2e_B1.1 + co2e_B1.2).
 */
export const getCO2eUnitsFromLifecycle = (lifecycle: Lifecycle): CO2eUnit[] =>
  getKeys(co2eLifecycleLabels).filter(
    (unit) => co2eLifecycleLabels[unit] === lifecycle,
  );

/**
 * Stage label for a single CO2e unit, e.g. `co2e_A1-A3` → `A1-A3`, `co2e_B1.1` → `B1.1`.
 * Unlike {@link co2eLifecycleLabels} this keeps the B1 sub-units apart, so callers that
 * report per-unit values (e.g. the spreadsheet export) can label carbonation (B1.1) and
 * refrigerant leakage (B1.2) separately.
 */
export const getCO2eUnitStageLabel = (unit: CO2eUnit): string =>
  unit.slice(CO2E_PREFIX.length);

/**
 * Stage label for a single cost unit, e.g. `sek_A1-A3` → `A1-A3`.
 * The cost counterpart of {@link getCO2eUnitStageLabel}: deriving the label from the unit
 * rather than from its lifecycle keeps one column per unit even if a lifecycle ever gains
 * a second cost unit, the way B1 has two CO2e units.
 */
export const getCostUnitStageLabel = (unit: CostUnit): string =>
  unit.slice(COST_PREFIX.length);

/**
 * Get all cost units belonging to a lifecycle.
 * Unlike {@link getCostUnitFromLifecycle} this returns an empty array for
 * lifecycles without a cost dimension (B1, B7).
 */
export const getCostUnitsFromLifecycle = (lifecycle: Lifecycle): CostUnit[] =>
  getKeys(costLifecycleLabels).filter(
    (unit) => costLifecycleLabels[unit] === lifecycle,
  );

export const omitLifecyclesFromResults = (
  results: Results,
  excludeLifecycles: Lifecycle[],
): Results => {
  const costUnits = excludeLifecycles.flatMap(getCostUnitsFromLifecycle);
  const co2eUnits = excludeLifecycles.flatMap(getCO2eUnitsFromLifecycle);

  return omit(results, ...costUnits, ...co2eUnits);
};

/**
 * Filter results to only include selected lifecycles.
 */
export const filterResultsByLifecycles = (
  results: Results,
  includeLifecycles: Lifecycle[],
): Results => {
  const excludeLifecycles = lifecycles.filter(
    (lifecycle) => !includeLifecycles.includes(lifecycle),
  );
  // Drop any spread SBEF group buckets: a lifecycle-filtered row is display-oriented
  // and flat, so the (unfiltered) breakdown must never ride along downstream.
  const flat = sbefScopeGroupKeys.some((key) => key in results)
    ? omit(results as SbefGroupResults, ...sbefScopeGroupKeys)
    : results;
  return omitLifecyclesFromResults(flat, excludeLifecycles);
};
